/**
 * Network diagnostic utilities to help identify SSL and connectivity issues
 */

export interface NetworkDiagnosticResult {
  endpoint: string;
  success: boolean;
  error?: string;
  responseTime?: number;
  statusCode?: number;
  sslInfo?: {
    protocol?: string;
    cipher?: string;
    valid?: boolean;
  };
}

export class NetworkDiagnostic {
  /**
   * Test connectivity to an endpoint
   */
  static async testEndpoint(url: string, timeout: number = 10000): Promise<NetworkDiagnosticResult> {
    const startTime = Date.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      return {
        endpoint: url,
        success: response.ok,
        statusCode: response.status,
        responseTime,
        sslInfo: this.extractSSLInfo(response)
      };
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      
      return {
        endpoint: url,
        success: false,
        error: error.message || 'Unknown error',
        responseTime
      };
    }
  }

  /**
   * Test multiple endpoints and return results
   */
  static async testMultipleEndpoints(urls: string[]): Promise<NetworkDiagnosticResult[]> {
    const promises = urls.map(url => this.testEndpoint(url));
    return await Promise.all(promises);
  }

  /**
   * Diagnose API connectivity issues
   */
  static async diagnoseAPIConnectivity(baseUrl: string): Promise<{
    results: NetworkDiagnosticResult[];
    recommendations: string[];
  }> {
    const testUrls = [
      `${baseUrl.replace('/api', '')}/health`,
      `${baseUrl}/query/info`,
      `${baseUrl.replace('https://', 'http://')}/health`, // HTTP fallback test
    ];

    const results = await this.testMultipleEndpoints(testUrls);
    const recommendations = this.generateRecommendations(results);

    return { results, recommendations };
  }

  /**
   * Extract SSL information from response (limited in browser)
   */
  private static extractSSLInfo(response: Response): any {
    // In browser environment, we have limited access to SSL details
    // We can infer some information from the response
    const url = new URL(response.url);
    
    return {
      protocol: url.protocol,
      valid: response.ok && url.protocol === 'https:',
    };
  }

  /**
   * Generate recommendations based on test results
   */
  private static generateRecommendations(results: NetworkDiagnosticResult[]): string[] {
    const recommendations: string[] = [];
    
    const httpsResults = results.filter(r => r.endpoint.startsWith('https://'));
    const httpResults = results.filter(r => r.endpoint.startsWith('http://'));
    
    const httpsSuccess = httpsResults.some(r => r.success);
    const httpSuccess = httpResults.some(r => r.success);
    
    if (!httpsSuccess && httpSuccess) {
      recommendations.push('HTTPS connections are failing but HTTP works. This suggests an SSL/TLS issue.');
      recommendations.push('Try using HTTP fallback or check your network proxy settings.');
    }
    
    if (!httpsSuccess && !httpSuccess) {
      recommendations.push('Both HTTPS and HTTP connections are failing. This suggests a network connectivity issue.');
      recommendations.push('Check your internet connection and firewall settings.');
    }
    
    const sslErrors = results.filter(r => 
      r.error && (
        r.error.includes('SSL') || 
        r.error.includes('certificate') || 
        r.error.includes('ERR_SSL_PROTOCOL_ERROR')
      )
    );
    
    if (sslErrors.length > 0) {
      recommendations.push('SSL/TLS errors detected. This may be caused by:');
      recommendations.push('- Corporate firewall or proxy intercepting HTTPS traffic');
      recommendations.push('- Outdated browser or system certificates');
      recommendations.push('- Network security software blocking connections');
      recommendations.push('Try disabling VPN/proxy temporarily or contact your IT administrator.');
    }
    
    const timeoutErrors = results.filter(r => 
      r.error && (r.error.includes('timeout') || r.error.includes('aborted'))
    );
    
    if (timeoutErrors.length > 0) {
      recommendations.push('Connection timeout detected. Try:');
      recommendations.push('- Checking your internet connection speed');
      recommendations.push('- Disabling browser extensions that might block requests');
      recommendations.push('- Trying from a different network');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('All connectivity tests passed. The issue may be intermittent.');
    }
    
    return recommendations;
  }

  /**
   * Check if the current environment might have SSL issues
   */
  static detectPotentialSSLIssues(): {
    hasProxy: boolean;
    isLocalhost: boolean;
    userAgent: string;
    recommendations: string[];
  } {
    const hasProxy = this.detectProxy();
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
    const userAgent = navigator.userAgent;
    
    const recommendations: string[] = [];
    
    if (hasProxy) {
      recommendations.push('Proxy detected. This may interfere with HTTPS connections.');
    }
    
    if (isLocalhost) {
      recommendations.push('Running on localhost. Consider testing on the deployed frontend.');
    }
    
    return {
      hasProxy,
      isLocalhost,
      userAgent,
      recommendations
    };
  }

  /**
   * Attempt to detect if a proxy is being used
   */
  private static detectProxy(): boolean {
    // This is a best-effort detection in browser environment
    // We can't directly detect proxy settings, but we can look for indicators
    
    // Check for common proxy-related headers or behaviors
    // In browser, this is limited, but we can check some indicators
    
    return false; // Browser environment has limited proxy detection
  }

  /**
   * Test if SSL fallback should be used
   */
  static async shouldUseSSLFallback(httpsUrl: string, httpUrl: string): Promise<boolean> {
    try {
      // Quick test of HTTPS endpoint
      const httpsResult = await this.testEndpoint(httpsUrl, 5000);
      
      if (httpsResult.success) {
        return false; // HTTPS works, no need for fallback
      }
      
      // Test HTTP fallback
      const httpResult = await this.testEndpoint(httpUrl, 5000);
      
      return httpResult.success; // Use fallback if HTTP works
    } catch (error) {
      return true; // If testing fails, try fallback
    }
  }
}

/**
 * Simple connectivity test for quick checks
 */
export async function quickConnectivityTest(url: string): Promise<boolean> {
  try {
    const result = await NetworkDiagnostic.testEndpoint(url, 5000);
    return result.success;
  } catch (error) {
    return false;
  }
}
